/**
 * Custom cart drawer(s) for Dawn: supports multiple [data-custom-cart-root].
 * Fixes PDP add-to-cart: section HTML can briefly lag the session; we retry and use cartData from pubsub.
 * Depends on: constants.js, pubsub.js, global.js (fetchConfig), theme.liquid window.routes.
 */
(function () {
  const ROOT_SELECTOR = '[data-custom-cart-root]';
  const DEFAULT_SECTION_ID = 'custom-cart-drawer';
  const SECTION_FETCH_RETRIES = 6;
  const SECTION_RETRY_BASE_MS = 55;
  const CART_SHOW_CLASS = 'cart__show';
  /** Clicks on these nodes do not remove cart__show (header cart icon / open toggle / wrapper). */
  const CART_CLOSE_IGNORE_SELECTOR =
    '#cart-icon-bubble, [data-custom-cart-toggle], [data-custom-cart-ignore-close]';

  function cartJsonUrl() {
    const u = window.routes?.cart_url || '/cart';
    if (u.endsWith('.js')) return u;
    return `${String(u).replace(/\/$/, '')}.js`;
  }

  /**
   * All cart roots (supports legacy #custom-cart if present without data attribute).
   */
  function getCartRoots() {
    const set = new Set();
    document.querySelectorAll(ROOT_SELECTOR).forEach((el) => set.add(el));
    const legacy = document.getElementById('custom-cart');
    if (legacy && legacy.classList.contains('custom-cart')) set.add(legacy);
    return Array.from(set);
  }

  function getSectionId() {
    const first = document.querySelector(ROOT_SELECTOR) || document.getElementById('custom-cart');
    const id = first && first.dataset && first.dataset.sectionId && String(first.dataset.sectionId).trim();
    return id || DEFAULT_SECTION_ID;
  }

  /**
   * Lets product-form.js stay on the page (no redirect to /cart) when Dawn drawer/notification is off.
   * @see assets/product-form.js resolveCartTarget
   */
  const productFormCartBridge = {
    getSectionsToRender() {
      return [{ id: 'cart-icon-bubble' }];
    },
    setActiveElement() {},
    renderContents() {},
  };
  Object.assign(productFormCartBridge, {
    hasCustomCart() {
      return getCartRoots().length > 0;
    },
    getCartRoots,
  });
  window.ShopifyCustomCartProductFormBridge = productFormCartBridge;

  function clearCartShowIfEmpty(cartOrItemCount) {
    const count =
      typeof cartOrItemCount === 'number'
        ? cartOrItemCount
        : typeof cartOrItemCount?.item_count === 'number'
          ? cartOrItemCount.item_count
          : null;
    if (count !== 0) return;
    getCartRoots().forEach((root) => root.classList.remove(CART_SHOW_CLASS));
  }

  /**
   * Only called after a successful product-form add (see onCartUpdateFromElsewhere).
   * Uses queueMicrotask so it runs after refresh/replaceWith updates the DOM.
   */
  function openCartDrawerAfterProductFormAdd() {
    queueMicrotask(() => {
      getCartRoots().forEach((root) => root.classList.add(CART_SHOW_CLASS));
    });
  }

  function setCartRootsLoading(roots, loading) {
    roots.forEach((root) => {
      root.classList.toggle('custom-cart--updating', loading);
      root.setAttribute('aria-busy', loading ? 'true' : 'false');
    });
  }

  function isClickOutsideCartDrawer(target) {
    if (!target || !target.closest) return true;
    if (target.closest(ROOT_SELECTOR) || target.closest('#custom-cart.custom-cart')) return false;
    if (target.closest(CART_CLOSE_IGNORE_SELECTOR)) return false;
    return true;
  }

  function closeCartDrawerIfOutsideClick(target) {
    const hasOpen = getCartRoots().some((r) => r.classList.contains(CART_SHOW_CLASS));
    if (!hasOpen || !isClickOutsideCartDrawer(target)) return;
    getCartRoots().forEach((r) => r.classList.remove(CART_SHOW_CLASS));
  }

  async function fetchCartJson() {
    const res = await fetch(cartJsonUrl(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart request failed');
    return res.json();
  }

  /**
   * Whether rendered section markup matches cart item_count (session vs Liquid).
   */
  function sectionMarkupMatchesItemCount(sectionRoot, itemCount) {
    const n = typeof itemCount === 'number' ? itemCount : 0;
    const hasEmptyState = !!sectionRoot.querySelector('.custom-cart__heading-empty');
    const hasLines = sectionRoot.querySelectorAll('.custom-cart__line[data-line-key]').length > 0;
    if (n === 0) return hasEmptyState && !hasLines;
    return !hasEmptyState && hasLines;
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Fetches section HTML until it matches expected item_count (retries for session lag after /cart/add).
   */
  async function fetchSectionCartMarkupWhenReady(expectedItemCount, cartHint) {
    let last = null;
    let expected = typeof expectedItemCount === 'number' ? expectedItemCount : null;
    let resolvedCart = cartHint;

    for (let attempt = 0; attempt < SECTION_FETCH_RETRIES; attempt++) {
      if (attempt > 0) {
        await sleep(SECTION_RETRY_BASE_MS * attempt);
      }
      if (expected === null) {
        try {
          const c =
            resolvedCart && typeof resolvedCart.item_count === 'number'
              ? resolvedCart
              : await fetchCartJson();
          resolvedCart = c;
          expected = c.item_count;
        } catch {
          expected = 0;
        }
      }

      last = await fetchSectionCartMarkupOnce();
      if (sectionMarkupMatchesItemCount(last, expected)) {
        return { root: last, cart: resolvedCart || { item_count: expected } };
      }

      expected = null;
      resolvedCart = null;
    }

    try {
      resolvedCart = await fetchCartJson();
    } catch {
      resolvedCart = { item_count: 0 };
    }
    if (last && !sectionMarkupMatchesItemCount(last, resolvedCart.item_count)) {
      console.warn('[custom-drawer-cart] Cart drawer HTML may be out of sync after add to cart; try reloading.');
    }
    return { root: last, cart: resolvedCart };
  }

  async function fetchSectionCartMarkupOnce() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', getSectionId());
    const res = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not load cart section');
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next =
      doc.querySelector(ROOT_SELECTOR) ||
      doc.querySelector('#custom-cart.custom-cart') ||
      doc.querySelector('.custom-cart');
    if (!next) throw new Error('Cart markup missing in section response');
    return next;
  }

  /**
   * Updates header bubble + every [data-cart-item-count] in the document.
   * @param {object} [cart] - Cart JSON. If omitted, fetches /cart.js.
   */
  async function syncCartUi(cart) {
    let data = cart;
    if (!data) {
      try {
        data = await fetchCartJson();
      } catch (e) {
        console.error('[custom-drawer-cart] syncCartUi', e);
        return;
      }
    }

    const count = typeof data.item_count === 'number' ? data.item_count : 0;

    clearCartShowIfEmpty(count);

    document.querySelectorAll('[data-cart-item-count]').forEach((el) => {
      const n = String(count);
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
        el.value = n;
      } else {
        el.textContent = n;
      }
      el.setAttribute('data-cart-item-count', n);
    });

    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;

    let bubble = link.querySelector('.cart-count-bubble');
    if (count === 0) {
      bubble?.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      link.appendChild(bubble);
    }
    let n = bubble.querySelector('span[aria-hidden="true"]');
    if (!n) {
      n = document.createElement('span');
      n.setAttribute('aria-hidden', 'true');
      bubble.insertBefore(n, bubble.firstChild);
    }
    n.textContent = count < 100 ? String(count) : '';
  }

  /**
   * @param {object} [cartHint] - e.g. product-form publish payload cartData, or /cart/change response
   */
  async function refreshCustomCartFromServer(cartHint) {
    const roots = getCartRoots();
    if (!roots.length) {
      await syncCartUi(cartHint);
      return;
    }

    const hintedCount = cartHint && typeof cartHint.item_count === 'number' ? cartHint.item_count : null;

    setCartRootsLoading(roots, true);
    try {
      const { root: next, cart: resolvedCart } = await fetchSectionCartMarkupWhenReady(hintedCount, cartHint);
      if (!next) throw new Error('No section markup');
      roots.forEach((old) => {
        if (!old.parentNode) return;
        old.replaceWith(next.cloneNode(true));
      });
      await syncCartUi(resolvedCart);
    } catch (err) {
      console.error('[custom-drawer-cart]', err);
      getCartRoots().forEach((r) => {
        if (r.isConnected) setCartRootsLoading([r], false);
      });
      await syncCartUi(cartHint);
      return;
    }

    setCartRootsLoading(getCartRoots(), false);
  }

  async function changeLine(lineKey, quantity) {
    const q = Math.max(0, parseInt(quantity, 10) || 0);
    const roots = getCartRoots();
    if (roots.length) setCartRootsLoading(roots, true);
    try {
      const res = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: lineKey, quantity: q }),
      });
      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(!res.ok ? raw.slice(0, 120) : 'Invalid cart response');
      }
      if (!res.ok && data.message) {
        throw new Error(data.message);
      }
      if (data.errors) {
        const msg = typeof data.errors === 'string' ? data.errors : data.errors?.join?.(', ') || 'Cart error';
        throw new Error(msg);
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'custom-cart-drawer', cartData: data });
      }
      await refreshCustomCartFromServer(data);
      return data;
    } catch (err) {
      setCartRootsLoading(getCartRoots(), false);
      throw err;
    }
  }

  function lineArticle(el) {
    return el && el.closest && el.closest('.custom-cart__line[data-line-key]');
  }

  function onDocumentClick(e) {
    closeCartDrawerIfOutsideClick(e.target);

    const cartRoot = e.target.closest(ROOT_SELECTOR) || e.target.closest('#custom-cart.custom-cart');
    if (!cartRoot) return;

    const removeBtn = e.target.closest('.custom-cart__line-remove');
    if (removeBtn) {
      e.preventDefault();
      const article = lineArticle(removeBtn);
      if (!article) return;
      changeLine(article.dataset.lineKey, 0).catch((err) => console.error(err));
      return;
    }

    const qtyBtn = e.target.closest('.custom-cart__line-qty-btn[data-qty-delta]');
    if (qtyBtn) {
      e.preventDefault();
      const form = qtyBtn.closest('.custom-cart__line-qty');
      const article = lineArticle(qtyBtn);
      if (!form || !article) return;
      const input = form.querySelector('.custom-cart__line-qty-input');
      let v = parseInt(input && input.value, 10) || 0;
      const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
      v = Math.max(0, v + delta);
      changeLine(article.dataset.lineKey, v).catch((err) => console.error(err));
    }
  }

  function onDocumentSubmit(e) {
    const form = e.target.closest('.custom-cart__line-qty');
    if (!form) return;
    const inCart =
      form.closest(ROOT_SELECTOR) || form.closest('#custom-cart.custom-cart');
    if (!inCart) return;
    e.preventDefault();
    const article = lineArticle(form);
    if (!article) return;
    const input = form.querySelector('.custom-cart__line-qty-input');
    const v = parseInt(input && input.value, 10) || 0;
    changeLine(article.dataset.lineKey, v).catch((err) => console.error(err));
  }

  function onDocumentQtyChange(e) {
    const input = e.target.closest('.custom-cart__line-qty-input');
    if (!input) return;
    const inCart = input.closest(ROOT_SELECTOR) || input.closest('#custom-cart.custom-cart');
    if (!inCart) return;
    const form = input.closest('.custom-cart__line-qty');
    const article = lineArticle(input);
    if (!form || !article) return;
    const v = parseInt(input.value, 10) || 0;
    changeLine(article.dataset.lineKey, v).catch((err) => console.error(err));
  }

  async function onCartUpdateFromElsewhere(event) {
    if (event && event.source === 'custom-cart-drawer') return;
    const hinted = event && event.cartData;
    const fromProductAdd = event && event.source === 'product-form';

    if (getCartRoots().length) {
      await refreshCustomCartFromServer(hinted);
    } else {
      await syncCartUi(hinted);
    }

    if (fromProductAdd) {
      openCartDrawerAfterProductFormAdd();
    }
  }

  function bindPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdateFromElsewhere);
  }

  function init() {
    const hasRoots = getCartRoots().length > 0;
    const hasCountTargets = document.querySelectorAll('[data-cart-item-count]').length > 0;

    if (!hasRoots && !hasCountTargets) return;

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('submit', onDocumentSubmit);
    document.addEventListener('change', onDocumentQtyChange);

    bindPubSub();
    syncCartUi().catch((err) => console.error(err));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
